import React from 'react';
import AgentTabLoading from './agent-tab-loading';

interface AgentDataTableProps {
  columns: React.ReactNode[];
  gridClassName: string;
  loading: boolean;
  empty: boolean;
  emptyText: string;
  children: React.ReactNode;
  className?: string;
  headerRowClassName?: string;
  headerCellBaseClassName?: string;
  headerCellClassName?: string;
  skeletonRows?: number;
}

const AgentDataTableSkeleton = ({
  columnCount,
  gridClassName,
  rows,
}: {
  columnCount: number;
  gridClassName: string;
  rows: number;
}) => {
  return (
    <div aria-hidden="true" className="relative z-[1] animate-pulse">
      {Array.from({length: rows}).map((_, rowIndex) => (
        <div
          key={rowIndex}
          className={[
            'grid h-10 items-center px-3',
            gridClassName,
            rowIndex % 2 === 1 ? 'bg-[rgba(140,140,140,0.06)]' : '',
          ].join(' ')}>
          {Array.from({length: columnCount}).map((__, cellIndex) => (
            <div
              key={cellIndex}
              className="mx-auto h-3 w-3/5 rounded-full bg-[#FFE8A1]/70"
            />
          ))}
        </div>
      ))}
    </div>
  );
};

const AgentDataTable = ({
  columns,
  gridClassName,
  loading,
  empty,
  emptyText,
  children,
  className = 'mt-2',
  headerRowClassName = 'h-[1.875rem] rounded-t-[0.5rem] bg-[#FFEDB5] px-3 shadow-[inset_0_0.0625rem_0_rgba(121,251,255,0.07)]',
  headerCellBaseClassName = 'min-w-0 text-center text-[0.75rem] font-semibold leading-[0.9375rem] tracking-[-0.01875rem] text-[#564715]',
  headerCellClassName = 'truncate',
  skeletonRows = 4,
}: AgentDataTableProps) => {
  const showSkeleton = loading && empty;

  return (
    <div
      className={[
        'relative min-h-[5rem] overflow-hidden rounded-[0.625rem] border-[0.5px] border-white/25 bg-white',
        className,
      ].join(' ')}>
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-0 top-[1.875rem] z-[2] rounded-b-[0.625rem] border-x-[0.5px] border-b-[0.5px] border-white/25"
      />
      <AgentTabLoading active={loading && !showSkeleton} />
      <div
        className={[
          'relative z-[1] grid items-center',
          headerRowClassName,
          gridClassName,
        ].join(' ')}>
        {columns.map((column, index) => (
          <div
            key={`${column}-${index}`}
            className={[headerCellBaseClassName, headerCellClassName].join(
              ' ',
            )}>
            {column}
          </div>
        ))}
      </div>
      <div className="relative z-[1]">
        {showSkeleton ? (
          <AgentDataTableSkeleton
            columnCount={columns.length}
            gridClassName={gridClassName}
            rows={skeletonRows}
          />
        ) : (
          children
        )}
        {empty && !loading && (
          <div className="flex h-20 items-center justify-center text-[0.75rem] text-[#857866]">
            {emptyText}
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentDataTable;
